import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef, ElementType } from "react";

import { cn } from "helpers/cn";

const textVariants = cva("break-words text-black/88", {
  variants: {
    variant: {
      body: "text-body",
      bodySmall: "text-body-small",
      caption: "text-caption",
      captionSmall: "text-caption-small",
      captionXSmall: "text-caption-xsmall"
    },
    weight: {
      regular: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold"
    },
    color: {
      foreground: "text-foreground",
      inherit: "text-inherit",
      secondary: "text-secondary",
      subtle: "text-subtle",
      muted: "text-muted-foreground",
      white: "text-white",
      destructive: "text-destructive",
      warning: "text-heat-foreground",
      success: "text-success",
      successText: "text-success-text",
      brand: "text-brand",
      brandDark: "text-brand-dark",
      disabled: "text-disabled"
    },
    align: {
      left: "block text-left",
      center: "block text-center",
      right: "block text-right"
    },
    transform: {
      uppercase: "uppercase",
      capitalize: "capitalize"
    },
    truncate: { true: "inline-block max-w-full truncate align-bottom" },
    lineThrough: { true: "line-through" },
    disabled: { true: "cursor-not-allowed text-disabled" }
  },
  defaultVariants: {
    variant: "body",
    weight: "regular"
  }
});

type TextVariantProps = VariantProps<typeof textVariants>;

type TextProps<T extends ElementType = "span"> = {
  as?: T;
} & TextVariantProps &
  Omit<ComponentPropsWithRef<T>, "as" | keyof TextVariantProps>;

const Text = <T extends ElementType = "span">({
  className,
  as,
  variant,
  weight,
  color,
  align,
  transform,
  truncate,
  lineThrough,
  disabled,
  ...props
}: TextProps<T>) => {
  const Comp = (as ?? "span") as ElementType;

  return (
    <Comp
      className={cn(
        textVariants({
          variant,
          weight,
          color,
          align,
          transform,
          truncate,
          lineThrough,
          disabled
        }),
        className
      )}
      data-slot="text"
      {...props}
    />
  );
};

export { Text, textVariants };
export type { TextProps };
